import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from 'styled-components';
import { object, mixed } from 'yup';
import { CreateSignedDistUploadId } from '../../../../../../common/src/legacy/services/apiRouterService';

// Components
import RegistrationForm from '../../../ui/advisory-flow/assets/RegistrationForm';
import InvoiceEN from '../../../../../../../assets/images/InvoiceEN';
import UploadForm from './UploadForm';

// Types
import { SmtFile } from '../../../../../../../types/Form';
import DriversLicense from '../../../ui/advisory-flow/assets/DriversLicense';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import useUploadFile, {
  UploadFunctionRes,
} from '../../../../../../common/src/hooks/useUploadFile';
import { useFormContext } from 'react-hook-form';
import { useFlowsDispatch } from '../../../../../../common/src/utils/globalVariables';
import { setCanSubmit } from '../../../../store/redux/slices/flowsSlice';
import { handleResponseLanguage } from '../../../../../../../i18n';
import { Center } from '@wegroup/design-system';

interface Props {
  question: FlowsQuestion;
}

const LinkInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
}) => {
  const { t } = useTranslation();
  const theme = useContext(ThemeContext);
  const { setError, clearErrors } = useFormContext();
  const flowsDispatch = useFlowsDispatch();

  const [isUploading, setIsUploading] = useState<boolean>(false);

  const upload = useUploadFile(
    ({ distributionId, filename }) =>
      CreateSignedDistUploadId(distributionId, {
        filename,
      }) as UploadFunctionRes,
  );

  const {
    field: { value, onBlur },
    error,
  } = useFlowInputComponent({ question });

  const fileName = question.field || question.id;

  const supportedFormats = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'application/pdf',
  ];

  const getUploadFormIcon = (field?: string) => {
    switch (field) {
      case 'driver_license':
        return <DriversLicense />;
      case 'invoice':
        return <InvoiceEN colors={theme} />;
      default:
        return <RegistrationForm />;
    }
  };

  const validationSchema = object().shape({
    [fileName]: mixed()
      .nullable()
      .test('fileType', t('Unsupported File Format'), (value) => {
        if (!value?.filename) return true;
        return supportedFormats.includes(value.filetype);
      }),
  });

  const clearFile = (key: string) => {
    onBlur({
      name: question.id,
      value: undefined,
    });
  };

  const uploadFile = async ({ file, filename, filetype, size }: SmtFile) => {
    const uploadObject = await upload(
      new File([file as Blob], filename, { type: filetype }),
    );
    return {
      document_id: uploadObject.document_id,
      location: uploadObject.location,
      filename,
      size,
      filetype,
      public_bucket_link: uploadObject.public_bucket_link,
      public_url: uploadObject.public_url,
    };
  };

  const startUploadFlag = () => {
    flowsDispatch(setCanSubmit(false));
    setIsUploading(true);
  };

  const stopUploadFlag = () => {
    flowsDispatch(setCanSubmit(true));
    setIsUploading(false);
  };

  const handleChange = async (name: string, file?: SmtFile) => {
    if (!file) return;
    startUploadFlag();
    try {
      validationSchema.validateSync({ [name]: file });
      onBlur({
        name: question.id,
        value: { key: name, ...(await uploadFile(file)) },
      });

      clearErrors(fileName);
    } catch (e) {
      setError(fileName, {
        type: 'manual',
        message: t('Looks like something went wrong'),
      });
    } finally {
      stopUploadFlag();
    }
  };

  return (
    <Center>
      <UploadForm
        name={fileName}
        file={value}
        Icon={getUploadFormIcon(question.field)}
        title={handleResponseLanguage(question.title)}
        description={handleResponseLanguage(question.description)}
        error={error?.message}
        handleChange={(file) => handleChange(fileName, file)}
        clearFile={clearFile}
        isUploading={isUploading}
      />
    </Center>
  );
};

export default LinkInputComponent;
